import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, ShieldCheck, User, Power, FileText, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const inputCls = "w-full min-h-[44px] rounded-sm border border-ei-mist bg-white px-3 py-2 text-sm focus:border-ei-navy outline-none";

export default function Team() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales" });

  const [terms, setTerms] = useState("");
  const [termsMeta, setTermsMeta] = useState(null);
  const [termsSaving, setTermsSaving] = useState(false);

  const loadTerms = async () => {
    try {
      const { data } = await api.get("/settings/quote-terms");
      setTerms(data.body || "");
      setTermsMeta(data);
    } catch (e) {
      console.warn("Quote terms could not be loaded", e);
    }
  };
  const saveTerms = async () => {
    if (!terms.trim()) { toast.error("Terms cannot be empty"); return; }
    setTermsSaving(true);
    try {
      const { data } = await api.put("/settings/quote-terms", { body: terms });
      setTermsMeta(data);
      toast.success("Quote terms saved — they print on page 2 of every quote PDF");
    } catch (err) {
      toast.error(formatApiError(err, "Could not save terms"));
    } finally { setTermsSaving(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) {
      toast.error("Could not load team");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadTerms(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/staff", form);
      toast.success(`${form.name} added`);
      setForm({ name: "", email: "", password: "", role: "sales" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not add staff member"));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/staff/${s.id}`, { active: !s.active });
      toast.success(`${s.name} ${s.active ? "disabled" : "enabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update"));
    }
  };

  const changeRole = async (s, role) => {
    try {
      await api.patch(`/staff/${s.id}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not update role"));
    }
  };

  return (
    <div>
      <Seo title="Team — EI Battery Workspace" />
      <span className="ei-eyebrow">Administration</span>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Team & access</h1>
      <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl">
        Administrators manage authorised staff. Sales staff manage enquiries but cannot change access or security settings.
      </p>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-ei-mist rounded-md overflow-hidden">
          <div className="px-5 py-4 border-b border-ei-mist"><h2 className="font-display font-semibold text-ei-navy">Staff members</h2></div>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-ei-navy" /></div>
          ) : (
            <ul className="divide-y divide-ei-mist" data-testid="staff-list">
              {staff.map((s) => (
                <li key={s.id} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`staff-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-9 h-9 rounded-sm bg-ei-navy flex items-center justify-center flex-shrink-0">
                      {s.role === "admin" ? <ShieldCheck className="w-4 h-4 text-ei-gold" /> : <User className="w-4 h-4 text-ei-gold" />}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-ei-navy truncate">{s.name} {!s.active && <span className="text-xs text-red-700">(disabled)</span>}</div>
                      <div className="text-xs text-ei-graphite/60 truncate">{s.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select value={s.role} onChange={(e) => changeRole(s, e.target.value)} disabled={s.id === user.id} className="text-sm rounded-sm border border-ei-mist px-2 py-1.5 min-h-[40px] disabled:opacity-50" data-testid={`role-${s.id}`}>
                      <option value="sales">Sales</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button onClick={() => toggleActive(s)} disabled={s.id === user.id} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-2 rounded-sm border border-ei-mist hover:border-ei-navy disabled:opacity-40 min-h-[40px]" data-testid={`toggle-${s.id}`}>
                      <Power className="w-3.5 h-3.5" /> {s.active ? "Disable" : "Enable"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-ei-mist rounded-md p-5">
          <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><UserPlus className="w-4 h-4 text-ei-golddark" /> Add staff</h2>
          <form onSubmit={create} className="mt-4 space-y-3" data-testid="add-staff-form">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="staff-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} data-testid="staff-email" />
            <input required type="password" placeholder="Temporary password (min 8)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} data-testid="staff-password" />
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={inputCls} data-testid="staff-role">
              <option value="sales">Sales</option>
              <option value="admin">Administrator</option>
            </select>
            <button type="submit" disabled={creating} className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm hover:bg-ei-ink min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="create-staff">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add staff member
            </button>
          </form>
          <p className="mt-3 text-xs text-ei-graphite/55">There is no public staff registration. New accounts are created here by an administrator only.</p>
        </div>
      </div>

      {/* Quote terms & conditions */}
      <div className="mt-6 bg-white border border-ei-mist rounded-md p-5 sm:p-6" data-testid="quote-terms-editor">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><FileText className="w-4 h-4 text-ei-golddark" /> Quote terms &amp; conditions</h2>
          {termsMeta && (
            <span className="text-xs text-ei-graphite/55">
              {termsMeta.is_default ? "Showing default terms" : `Last edited by ${termsMeta.updated_by || "—"}`}
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-ei-graphite/70 max-w-3xl">
          This text prints as a second page on every quote PDF. Put each clause heading on its own short line, followed by the clause text. Blank lines add spacing.
        </p>
        <textarea
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          rows={16}
          spellCheck
          className="mt-4 w-full rounded-sm border border-ei-mist bg-white px-3 py-3 text-sm leading-relaxed focus:border-ei-navy outline-none font-mono"
          data-testid="quote-terms-input"
        />
        <div className="mt-3 flex items-center gap-3">
          <button onClick={saveTerms} disabled={termsSaving} className="font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm hover:bg-ei-ink min-h-[44px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="quote-terms-save">
            {termsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save terms
          </button>
          <span className="text-xs text-ei-graphite/50">Editable by administrators only.</span>
        </div>
      </div>
    </div>
  );
}
